import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { QueueDisplayScreen } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { useOfficesFormStyles } from '../Offices.Styles';
import {
  findDisplayByCode,
  formatRegistrationCode,
  isDisplayPaired,
  isValidRegistrationCode,
  useRequestOutcome,
} from '../Offices.Functions';
import { ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';

export const PAIRING_POLL_MS = 5000;
export const PAIRING_TIMEOUT_MS = 3 * 60 * 1000;

export interface DisplayPairingDialogProps {
  open: boolean;
  displays: QueueDisplayScreen[];
  defaults?: { sedeId?: number | null; sectorId?: number | null };
  isCreating: boolean;
  createFailed: boolean;
  error?: ErrorType;
  onCreate: (payload: QueueDisplayScreen) => void;
  onPoll: (id: number) => void;
  onPaired: (display: QueueDisplayScreen) => void;
  onClose: () => void;
}

type Phase = 'input' | 'creating' | 'waiting' | 'timeout' | 'failed';

/**
 * "Vincular display": el administrador escribe el código que muestra la TV. Si el display no existe
 * se crea con ese código; si ya existe (cargado con "Nuevo display") se usa directamente (B15). Luego
 * se consulta el display hasta que la TV informa su huella.
 */
const DisplayPairingDialog = ({
  open,
  displays,
  defaults,
  isCreating,
  createFailed,
  error,
  onCreate,
  onPoll,
  onPaired,
  onClose,
}: DisplayPairingDialogProps) => {
  const { t } = useTranslation();
  const styles = useOfficesFormStyles();
  const [code, setCode] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [phase, setPhase] = useState<Phase>('input');
  const [targetId, setTargetId] = useState<number | undefined>(undefined);
  const interval = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTimers = useCallback(() => {
    if (interval.current) clearInterval(interval.current);
    if (timeout.current) clearTimeout(timeout.current);
    interval.current = null;
    timeout.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      stopTimers();
      setCode('');
      setShowErrors(false);
      setPhase('input');
      setTargetId(undefined);
    }
  }, [open, stopTimers]);

  useEffect(() => () => stopTimers(), [stopTimers]);

  const startWaiting = useCallback(
    (id: number) => {
      stopTimers();
      setTargetId(id);
      setPhase('waiting');
      interval.current = setInterval(() => onPoll(id), PAIRING_POLL_MS);
      timeout.current = setTimeout(() => {
        stopTimers();
        setPhase('timeout');
      }, PAIRING_TIMEOUT_MS);
    },
    [onPoll, stopTimers]
  );

  const armCreate = useRequestOutcome(isCreating, createFailed, (ok) => {
    if (!ok) {
      setPhase('failed');
      return;
    }
    const created = findDisplayByCode(displays, code);
    if (created?.id !== undefined) startWaiting(created.id);
    else setPhase('failed');
  });

  // Fin del proceso: la TV informó su huella.
  useEffect(() => {
    if (phase !== 'waiting' || targetId === undefined) return;
    const target = displays.find((d) => d.id === targetId);
    if (target && isDisplayPaired(target)) {
      stopTimers();
      onPaired(target);
    }
  }, [displays, phase, targetId, onPaired, stopTimers]);

  const codeError = !code.trim()
    ? 'Offices.validation.required'
    : !isValidRegistrationCode(code)
    ? 'Offices.validation.codeFormat'
    : undefined;
  const existing = code && !codeError ? findDisplayByCode(displays, code) : undefined;
  const alreadyPaired = existing && isDisplayPaired(existing);

  const register = () => {
    setShowErrors(true);
    if (codeError || alreadyPaired) return;
    if (existing?.id !== undefined) {
      startWaiting(existing.id);
      return;
    }
    setPhase('creating');
    armCreate();
    onCreate({
      registrationNumber: code.trim(),
      name: t('Offices.pairing.defaultName', { code: code.trim() }),
      sedeId: defaults?.sedeId ?? null,
      sectorId: defaults?.sectorId ?? null,
      isActive: true,
    });
  };

  const busy = phase === 'creating' || phase === 'waiting';

  return (
    <Dialog open={open} modalType="modal" onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('Offices.pairing.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <span className={styles.muted}>{t('Offices.pairing.description')}</span>
              {phase === 'failed' && <ErrorBanner title={t('Offices.pairing.failed')} error={error} testId="pairing-error" />}
              {phase === 'timeout' && (
                <GuideMessageBar intent="warning" data-testid="pairing-timeout">
                  <MessageBarBody>
                    <MessageBarTitle>{t('Offices.pairing.timeoutTitle')}</MessageBarTitle>
                    {t('Offices.pairing.timeout')}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
              <Field
                label={t('Offices.pairing.code')}
                required
                hint={existing && !alreadyPaired ? t('Offices.pairing.existingHint', { name: existing.name || existing.registrationNumber }) : t('Offices.pairing.codeHint')}
                validationState={showErrors && (codeError || alreadyPaired) ? 'error' : 'none'}
                validationMessage={
                  showErrors && codeError ? t(codeError) : showErrors && alreadyPaired ? t('Offices.pairing.alreadyPaired') : undefined
                }
              >
                <Input
                  size="large"
                  value={code}
                  disabled={busy}
                  placeholder="9999-9999"
                  inputMode="numeric"
                  onChange={(_, d) => setCode(formatRegistrationCode(d.value))}
                />
              </Field>
              {busy && (
                <div className={styles.readOnlyBox} role="status" data-testid="pairing-waiting">
                  <Spinner size="tiny" label={phase === 'creating' ? t('Offices.pairing.creating') : t('Offices.pairing.waiting')} />
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {phase === 'waiting' ? t('Offices.pairing.finishLater') : t('Offices.actions.cancel')}
            </Button>
            {phase === 'timeout' && targetId !== undefined ? (
              <Button appearance="primary" onClick={() => startWaiting(targetId)}>
                {t('Offices.actions.retry')}
              </Button>
            ) : (
              <Button appearance="primary" onClick={register} disabled={busy}>
                {t('Offices.pairing.submit')}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DisplayPairingDialog;
